import {
  Box,
  CircularProgress,
  MenuItem as MuiMenuItem,
  TextField,
  Tooltip,
} from '@mui/material'

import { Button } from 'Common/Supplyvue-ui'
import { PageLayout } from 'Common/Layout/PageLayout'
import { Panel } from 'Common/Components/Panel'
import { FormRow } from 'Common/Components/FormRow'
import { useEffect, useMemo, useState } from 'react'
import {
  useCreateScenario,
  useCreateScenarioConfig,
  useProjectData,
} from './Scenarios.data'
import { useNavigate, useParams } from 'react-router-dom'
import { Loader } from 'Common/Loader/Loader'
import dayjs, { Dayjs } from 'dayjs'
import { DatePickerMUI } from './Calender'
import { FormValuesType } from './Scenarios.types'
import { toast } from 'react-hot-toast'

export const HISTORICAL = 'Historical'
export const FORECAST = 'Forecast'

type MenuItem = {
  value: string | number
  label: string
}
const salesRateTypeDropDownValues: MenuItem[] = [
  {
    value: HISTORICAL,
    label: 'Historical',
  },
  {
    value: FORECAST,
    label: 'Forecast',
  },
]

const templateSelectionDropDownValues: MenuItem[] = [
  {
    value: 0,
    label: 'New template',
  },
  {
    value: 1,
    label: 'System of records',
  },
  {
    value: 2,
    label: 'Nudged template',
  },
]

const formValues: FormValuesType = {
  scenarioName: '',
  salesRatePeriodType: HISTORICAL,
  periodStartWeek: dayjs(),
  numberOfWeeks: 1,
  template: 0,
}

type Config = {
  maxDate: Dayjs
  minDate: Dayjs
  numWeeks: number
}
const config: Config = {
  maxDate: dayjs(),
  minDate: dayjs(),
  numWeeks: 0,
}

export const CreateScenarioPage = () => {
  const navigate = useNavigate()
  const { projectId } = useParams<{ projectId: string }>()
  const [formData, setFormData] = useState(formValues)
  const [configData, setConfigData] = useState(config)

  const { data, isLoading } = useCreateScenarioConfig(
    projectId as string,
    formData.salesRatePeriodType
  )

  const { data: projectData } = useProjectData(projectId)

  useEffect(() => {
    if (data) {
      const maxDate = dayjs(data.maxDate)
      const minDate = dayjs(data.minDate)
      setConfigData({
        maxDate: maxDate,
        minDate: minDate,
        numWeeks: data.numWeeks,
      })
      setFormData({
        ...formData,
        periodStartWeek: minDate,
      })
    }
  }, [data])

  const weekSelectionDropDown = useMemo(() => {
    const weeks = []
    const startWeek = formData.periodStartWeek
    const endweek = configData.maxDate
    const diff = endweek.diff(startWeek, 'week')
    for (let i = 1; i <= diff; i++) {
      weeks.push({
        value: i,
        label: `${i} weeks`,
      })
    }
    return weeks
  }, [formData.periodStartWeek, configData.maxDate])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }
  const handleDropDownChange = (name: string, value: string): void => {
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleCalendarInputChange = (name: string, value: Dayjs) => {
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const onScenarioCreateSuccess = () => {
    // Handle success logic here
    toast.success('Scenario created successfully')
    // Redirect to the scenarios page or show a success message
    navigate(`/projects/${projectId}`)
  }
  const onScenarioCreateFailure = () => {
    // Handle failure logic here
    toast.error('Scenario creation failed')
  }
  const { mutate: createScenario, isPending: isCreating } = useCreateScenario(
    onScenarioCreateSuccess,
    onScenarioCreateFailure
  )

  const handleCreateButtonClick = () => {
    // Handle button click logic here
    const sequenceId = projectData?.scenariosCount ?? 0
    const data = {
      ...formData,
      projectId: projectId as string,
      sequenceId: sequenceId,
    }
    createScenario(data)
  }
  const handleCancelButtonClick = () => {
    setFormData(formValues)
  }

  const shouldDisableDate = (date: Dayjs) => {
    return date.day() !== 1
  }

  const isCreateButtonDisabled = () => {
    for (const key in formData) {
      if (formData[key as keyof FormValuesType] === '') {
        return true
      }
    }
    return false
  }

  const incomplete = isCreateButtonDisabled()

  const select = (
    id: string,
    field: keyof FormValuesType,
    items: MenuItem[]
  ) => (
    <TextField
      select
      size="small"
      fullWidth
      id={id}
      value={formData[field] as string | number}
      onChange={(event) => handleDropDownChange(field, event.target.value)}
    >
      {items.map((item) => (
        <MuiMenuItem key={item.value} value={item.value}>
          {item.label}
        </MuiMenuItem>
      ))}
    </TextField>
  )

  return (
    <PageLayout
      title="Create scenario"
      subtitle={projectData?.projectName}
      actions={
        <>
          {/* It was labelled Cancel, but it has always reset the form rather
              than leaving the page. */}
          <Button variant="outlined" onClick={handleCancelButtonClick}>
            Reset
          </Button>
          <Tooltip title={incomplete ? 'Fill in every field to create it' : ''}>
            <span>
              <Button
                variant="contained"
                onClick={handleCreateButtonClick}
                disabled={incomplete || isCreating}
                startIcon={
                  isCreating ? (
                    <CircularProgress color="inherit" size={14} />
                  ) : undefined
                }
              >
                Create scenario
              </Button>
            </span>
          </Tooltip>
        </>
      }
    >
      {isLoading ? (
        <Loader />
      ) : (
        <Box
          component="form"
          aria-label="Create scenario"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault()
            if (!incomplete && !isCreating) handleCreateButtonClick()
          }}
          sx={{
            maxWidth: 640,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Panel title="General">
            <FormRow label="Scenario name" htmlFor="scenario-name">
              <TextField
                size="small"
                fullWidth
                id="scenario-name"
                name="scenarioName"
                placeholder="e.g. Q3 forecast, 8 weeks"
                value={formData.scenarioName}
                onChange={handleInputChange}
              />
            </FormRow>
          </Panel>

          <Panel title="Sales rate">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <FormRow label="Period type" htmlFor="sales-rate-period-type">
                {select(
                  'sales-rate-period-type',
                  'salesRatePeriodType',
                  salesRateTypeDropDownValues
                )}
              </FormRow>
              <FormRow label="Period start week" htmlFor="period-start-week">
                <DatePickerMUI
                  sx={{ width: '100%' }}
                  slotProps={{
                    textField: {
                      id: 'period-start-week',
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                  value={formData.periodStartWeek}
                  onChange={(value: Dayjs) =>
                    handleCalendarInputChange('periodStartWeek', value)
                  }
                  format="DD-MM-YYYY"
                  minDate={configData.minDate}
                  maxDate={configData.maxDate}
                  shouldDisableDate={shouldDisableDate}
                />
              </FormRow>
              <FormRow label="Number of weeks" htmlFor="number-of-weeks">
                {select(
                  'number-of-weeks',
                  'numberOfWeeks',
                  weekSelectionDropDown
                )}
              </FormRow>
            </Box>
          </Panel>

          <Panel title="Template">
            <FormRow label="Template" htmlFor="template-selection">
              {select(
                'template-selection',
                'template',
                templateSelectionDropDownValues
              )}
            </FormRow>
          </Panel>
          {/* Lets Enter in the name field submit the form. */}
          <button type="submit" hidden />
        </Box>
      )}
    </PageLayout>
  )
}
